import React from 'react'

import { Skeleton, useSlider } from '@fourthwall/components'
import { ChevronLeftIcon, ChevronRightIcon } from '@fourthwall/icons'

import type { MiniRecommendationsData } from './MiniRecommendations.type'
import * as Carousel from './components/Carousel/Carousel'
import { AbandonmentDiscountSlide } from './modules/AbandonmentDiscountSlide/AbandonmentDiscountSlide'
import { ClaimFreeDomainSlide } from './modules/ClaimFreeDomainSlide/ClaimFreeDomainSlide'
import { FreeShippingSlide } from './modules/FreeShippingSlide/FreeShippingSlide'
import { OrdersToSendSlide } from './modules/OrdersToSendSlide/OrdersToSendSlide'
import { ThankSupportersSlide } from './modules/ThankYousSlide/ThankYousSlide'
import { TwitchGiftingSlide } from './modules/TwitchGiftingSlide/TwitchGiftingSlide'
import { YTShelfSlide } from './modules/YTShelfSlide/YTShelfSlide'

type MiniRecommendationsProps = {
  data: MiniRecommendationsData
  page?: 'home' | 'orders' | 'promotions'
}

export const MiniRecommendations = ({ data, page = 'home' }: MiniRecommendationsProps) => {
  const {
    getContainerProps,
    getSliderProps,
    getSlideProps,
    getPrevButtonProps,
    getNextButtonProps,
    dataRole,
    resetSlider,
  } = useSlider()

  const slideProps = {
    ...getSlideProps(),
    data,
    resetSlider,
  }

  const { $slidesCount, ...containerProps } = getContainerProps()

  return (
    <Carousel.Root
      id="carousel-root"
      data-analytics="mini-recommendations-container"
      {...containerProps}
      $slidesCount={$slidesCount}
      $sliderDataRole={dataRole}
      data-empty={$slidesCount === 0 ? 'true' : 'false'}
    >
      <Carousel.ItemGradient $side="right" $isVisible={!getPrevButtonProps().disabled} />
      {(() => {
        const { disabled, ...prevButtonProps } = getPrevButtonProps()

        return (
          <Carousel.NavButton $direction="left" $isVisible={!disabled} {...prevButtonProps}>
            <ChevronLeftIcon width={18} />
          </Carousel.NavButton>
        )
      })()}

      <Carousel.Slider {...getSliderProps()}>
        {page === 'home' && (
          <>
            <OrdersToSendSlide {...slideProps} />
            <FreeShippingSlide {...slideProps} />
            <YTShelfSlide {...slideProps} />
            <ThankSupportersSlide {...slideProps} />
            <TwitchGiftingSlide {...slideProps} />
            <AbandonmentDiscountSlide {...slideProps} />
            <ClaimFreeDomainSlide {...slideProps} />
          </>
        )}
        {page === 'orders' && (
          <>
            <OrdersToSendSlide {...slideProps} />
            <FreeShippingSlide {...slideProps} />
            <ThankSupportersSlide {...slideProps} />
            <YTShelfSlide {...slideProps} />
            <TwitchGiftingSlide {...slideProps} />
            <AbandonmentDiscountSlide {...slideProps} />
            <ClaimFreeDomainSlide {...slideProps} />
          </>
        )}
        {page === 'promotions' && (
          <>
            <FreeShippingSlide {...slideProps} />
            <AbandonmentDiscountSlide {...slideProps} />
            <YTShelfSlide {...slideProps} />
            <OrdersToSendSlide {...slideProps} />
            <TwitchGiftingSlide {...slideProps} />
            <ThankSupportersSlide {...slideProps} />
            <ClaimFreeDomainSlide {...slideProps} />
          </>
        )}
      </Carousel.Slider>

      <Carousel.ItemGradient $side="left" $isVisible={!getNextButtonProps().disabled} />
      {(() => {
        const { disabled, ...nextButtonProps } = getNextButtonProps()

        return (
          <Carousel.NavButton $direction="right" $isVisible={!disabled} {...nextButtonProps}>
            <ChevronRightIcon width={18} />
          </Carousel.NavButton>
        )
      })()}
    </Carousel.Root>
  )
}

export const MiniRecommendationsSkeleton = () => {
  return (
    <Carousel.Root $slidesCount={3}>
      <div style={{ width: '100%' }}>
        <div style={{ width: '100%', display: 'flex', gap: 'var(--space-400)' }}>
          <Skeleton.Shape width="33%" height="36px" radius="var(--radius-brand)" />
          <Skeleton.Shape width="33%" height="36px" radius="var(--radius-brand)" />
          <Skeleton.Shape width="33%" height="36px" radius="var(--radius-brand)" />
        </div>
      </div>
    </Carousel.Root>
  )
}
